import {
    motion,
    useMotionValue,
    useMotionValueEvent,
    useTransform,
} from "framer-motion"
import React, { useState } from "react"
import { heartAnimations } from "./AnimateData"
import CardEffect from "./CardEffect"
import { cleanText } from "../../util/stringUtil"
import { getMatchPercentage } from "../../util/matchPercentageUtil"
import LazyImage from "../UIComponents/LazyImage"
import MonterContent from "../MonterContent/MonterContent"
import { ArrowLeft, Heart, Image, Info, X } from "react-feather"
import { useProfileStore } from "../../state-management/profileStore"

const CompanyCard = React.memo(
    ({ data, onDragChange, themeColor, handleLike, handleRejection }) => {
        const x = useMotionValue(0)
        const y = useMotionValue(0)
        const xInput = [-300, 0, 300]
        const [positive, setPositive] = useState(true)
        const [isAnimatingExit, setIsAnimatingExit] = useState(false)
        const [showInfo, setShowInfo] = useState(false)

        const scale = useTransform(x, xInput, [1.1, 1, 1.1], { clamp: true })
        const rotate = useTransform(x, [-150, 150], [-15, 15], { clamp: true })
        const background = useTransform(
            x,
            [-150, 0, 150],
            [
                `linear-gradient(180deg, #fb7185 0%, ${themeColor} 100%)`,
                `linear-gradient(180deg, ${themeColor} 0%, ${themeColor} 100%)`,
                `linear-gradient(180deg, #34d399 0%, ${themeColor} 100%)`,
            ],
        )

        const profileData = useProfileStore((state) => state.profileData)

        const SWIPE_THRESHOLD = 120

        const matchPercentage =
            getMatchPercentage(
                {
                    branscher: profileData.branscher,
                    tjanster: profileData.tjanster,
                },
                data,
            ) || 0

        useMotionValueEvent(x, "change", (latest) => {
            if (!isAnimatingExit) {
                setPositive(latest >= 0)
                onDragChange(background)
            }
        })

        const handleDragEnd = (event, info) => {
            const xVelocity = info.velocity.x
            const xValue = x.get()

            if (
                Math.abs(xValue) > SWIPE_THRESHOLD ||
                Math.abs(xVelocity) > 500
            ) {
                setIsAnimatingExit(true)

                if (xValue > 0) {
                    handleLike()
                } else {
                    handleRejection()
                }
            }
        }

        return (
            <>
                {/* Detta är montermodalen som visas när man klickar på info-knappen */}
                {showInfo ? (
                    <div className="fixed top-0 left-0 bottom-0 right-0 z-20 overflow-y-auto ">
                        <div className="relative pb-24 max-w-4xl mx-auto">
                            <MonterContent
                                data={data}
                                modalOpen={true}
                                hideLike={true}
                            />
                            <div className="fixed bottom-safe-nav p-4">
                                <button
                                    onClick={() => setShowInfo((prev) => !prev)}
                                    className={`size-12 bg-white/65 dark:bg-black/65 border dark:border-neutral-700 backdrop-blur-sm  rounded-full  flex items-center justify-center transition-all duration-150 hover:bg-white/75 dark:hover:bg-black/75 active:scale-95 active:bg-white/85 dark:active:bg-black/85 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                                >
                                    <ArrowLeft
                                        size={32}
                                        className="fg-color-1"
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    // Container för kortet och knapparna
                    <div className="w-80 z-40 absolute">
                        {/* Själva kortet */}
                        <motion.div
                            className=" bg-white flex flex-col  dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-300 dark:border-neutral-700 will-change-transform overflow-hidden"
                            drag
                            dragConstraints={{
                                left: 0,
                                right: 0,
                                top: 0,
                                bottom: 0,
                            }}
                            dragElastic={0.75}
                            initial={{ opacity: 0, y: 100 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{
                                opacity: 0,
                                x: positive ? 300 : -300,
                                y: -100,
                                transition: { duration: 0.3 },
                            }}
                            onDragEnd={handleDragEnd}
                            style={{
                                x,
                                y,
                                scale,
                                rotate,
                            }}
                            data-disable-swipe
                        >
                            {/* Matchprocent-mätaren */}
                            <div className="w-full p-4 fg-color-1 z-20">
                                <h2 className="font-semibold mb-1">
                                    {matchPercentage}% Match
                                </h2>
                                <div className="border border-primary-500 rounded-full overflow-hidden">
                                    <div
                                        style={{ width: `${matchPercentage}%` }}
                                        className=" bg-primary-500 h-2"
                                    ></div>
                                </div>
                            </div>
                            {/* Företagets logotyp */}
                            <div className="h-48 p-4 flex items-center justify-center bg-white relative overflow-hidden">
                                <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 aspect-square  flex items-center justify-center">
                                    {data.logotyp ? (
                                        <LazyImage
                                            src={data.logotyp}
                                            alt={"logo"}
                                            className=" p-4 object-contain rounded-xl"
                                            placeHolderClassName="flex-1 rounded rounded-xl"
                                        />
                                    ) : (
                                        // Placeholder om data för företagets logotyp saknas
                                        <div className="rotate-12 relative">
                                            <div className="absolute -top-1 -right-1 -rotate-12">
                                                <X
                                                    size={48}
                                                    strokeWidth={2}
                                                    className="text-red-200"
                                                />
                                            </div>
                                            <Image
                                                size={96}
                                                strokeWidth={1}
                                                className="text-neutral-200"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Företagets namn och info */}
                            <div className=" bg-gradient-to-b from-neutral-50 to-neutral-100 dark:from-neutral-800 dark:to-neutral-800 p-4 flex-1 border-t">
                                <div className="w-full ">
                                    <h2 className="text fg-color-1 font-bold leading-tight line-clamp-1 text-ellipsis">
                                        {data.organisation_namn}
                                    </h2>
                                </div>
                                <div className="w-full  mt-1">
                                    {data.katalogtext ? (
                                        <div className="h-20">
                                            <p className="text-sm text-neutral-700 dark:text-neutral-300 text-ellipsis line-clamp-4">
                                                {cleanText(data.katalogtext)}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="h-20 gap-1  w-full flex flex-col">
                                            {[...Array(4)].map((_, index) => (
                                                <div
                                                    key={index}
                                                    className="flex-1 bg-neutral-200/50 dark:bg-neutral-700/50  rounded"
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                        {/* Container för info - reject- och like-knapparna */}
                        <div className="p-4 flex gap-2 z-[100] justify-center">
                            {/* Reject-knapp */}
                            <button
                                onClick={() => {
                                    if (showInfo) return
                                    setPositive(false)
                                    handleRejection()
                                }}
                                className={`${showInfo && "opacity-0 pointer-events-none"} size-12 bg-red-500/65  backdrop-blur-sm  rounded-full  flex items-center justify-center transition-all duration-150 hover:bg-red-500/75 active:scale-95 active:bg-red-500/85 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                            >
                                <X size={24} className="text-white" />
                            </button>{" "}
                            {/* Infoknapp */}
                            <button
                                onClick={() => setShowInfo((prev) => !prev)}
                                className={`size-12 bg-white/65 dark:bg-black/65 border dark:border-neutral-700 backdrop-blur-sm  rounded-full  flex items-center justify-center transition-all duration-150 hover:bg-white/75 dark:hover:bg-black/75 active:scale-95 active:bg-white/85 dark:active:bg-black/85 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                            >
                                {!showInfo ? (
                                    <Info size={24} className="fg-color-2" />
                                ) : (
                                    <ArrowLeft
                                        size={32}
                                        className="fg-color-1"
                                    />
                                )}
                            </button>
                            {/* Like-knapp */}
                            <button
                                onClick={() => {
                                    if (showInfo) return
                                    setPositive(true)

                                    handleLike()
                                }}
                                className={`${showInfo && "opacity-0 pointer-events-none"} size-12 bg-pink-500/65  backdrop-blur-sm  rounded-full  flex items-center justify-center transition-all duration-150 hover:bg-pink-500/75 active:scale-95 active:bg-pink-500/85 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500`}
                            >
                                <Heart size={24} className="text-white" />
                            </button>
                        </div>
                        {heartAnimations.map((animations, index) => (
                            <CardEffect
                                key={index}
                                animations={animations}
                                x={x}
                                positive={positive}
                                xInput={xInput}
                            />
                        ))}
                    </div>
                )}
            </>
        )
    },
)

export default CompanyCard
